import { BACKEND_URL, getImageUrl } from '../utils/api';
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Plus, Trash2, X, Edit2, ChevronUp, ChevronDown } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import FileUpload from '../components/FileUpload';

const AdminGalleryNew = () => {
  const { getAuthHeader } = useAuth();
  const { toast } = useToast();
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUpload, setShowUpload] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [uploadData, setUploadData] = useState({
    title: '',
    image_url: '',
    alt_text: '',
    order: 0
  });

  useEffect(() => {
    fetchImages();
  }, []);

  const fetchImages = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/gallery`, {
        headers: getAuthHeader()
      });
      // Filter to only show gallery images and sort by order
      const galleryImages = response.data
        .filter(img => img.section === 'gallery' || img.section === 'action-gallery' || img.section === 'general')
        .sort((a, b) => (a.order || 0) - (b.order || 0));
      setImages(galleryImages);
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to fetch images', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!uploadData.title || !uploadData.image_url) {
      toast({ title: 'Error', description: 'Title and image are required', variant: 'destructive' });
      return;
    }

    try {
      const dataToSend = {
        ...uploadData,
        section: 'gallery',
        is_active: true,
        order: editingId ? uploadData.order : images.length
      };

      if (editingId) {
        await axios.put(
          `${BACKEND_URL}/api/admin/gallery/${editingId}`,
          dataToSend,
          { headers: getAuthHeader() }
        );
        toast({ title: 'Success', description: 'Image updated successfully' });
      } else {
        await axios.post(
          `${BACKEND_URL}/api/admin/gallery`,
          dataToSend,
          { headers: getAuthHeader() }
        );
        toast({ title: 'Success', description: 'Image uploaded successfully' });
      }

      setUploadData({ title: '', image_url: '', alt_text: '', order: 0 });
      setShowUpload(false);
      setEditingId(null);
      fetchImages();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to save image',
        variant: 'destructive'
      });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this image?')) return;

    try {
      await axios.delete(`${BACKEND_URL}/api/admin/gallery/${id}`, {
        headers: getAuthHeader()
      });
      toast({ title: 'Success', description: 'Image deleted successfully' });
      fetchImages();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to delete image', variant: 'destructive' });
    }
  };

  const handleEdit = (image) => {
    setEditingId(image.id);
    setUploadData({
      title: image.title,
      image_url: image.image_url,
      alt_text: image.alt_text || '',
      order: image.order || 0
    });
    setShowUpload(true);
  };

  const handleReorder = async (imageId, direction) => {
    const index = images.findIndex(img => img.id === imageId);
    
    if ((direction === 'up' && index === 0) || (direction === 'down' && index === images.length - 1)) {
      return;
    }

    const newIndex = direction === 'up' ? index - 1 : index + 1;
    const imageToMove = images[index];
    const imageToSwap = images[newIndex];

    try {
      await Promise.all([
        axios.put(
          `${BACKEND_URL}/api/admin/gallery/${imageToMove.id}`,
          { ...imageToMove, order: newIndex },
          { headers: getAuthHeader() }
        ),
        axios.put(
          `${BACKEND_URL}/api/admin/gallery/${imageToSwap.id}`,
          { ...imageToSwap, order: index },
          { headers: getAuthHeader() }
        )
      ]);

      fetchImages();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to reorder images', variant: 'destructive' });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-[#5a6f4e] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-2xl font-bold text-[#c5bfa9]">Gallery Management</h1>
          <p className="text-[#8b9a7e] mt-1">Upload images for the website carousel gallery</p>
        </div>
        <Button
          onClick={() => {
            setShowUpload(!showUpload);
            setEditingId(null);
            setUploadData({ title: '', image_url: '', alt_text: '', order: 0 });
          }}
          className="bg-[#5a6f4e] hover:bg-[#4a5f3e] text-white"
        >
          {showUpload ? <X size={20} className="mr-2" /> : <Plus size={20} className="mr-2" />}
          {showUpload ? 'Cancel' : 'Add Image'}
        </Button>
      </div>

      {/* Upload/Edit Form */}
      {showUpload && (
        <Card className="mb-8 bg-[#232921] border border-[#3d4a35] shadow-sm">
          <CardContent className="p-6">
            <h3 className="text-lg font-semibold mb-4 text-[#c5bfa9]">
              {editingId ? 'Edit Image' : 'Add New Gallery Image'}
            </h3>
            <form onSubmit={handleUpload} className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#8b9a7e] mb-1">
                    Title *
                  </label>
                  <input
                    type="text"
                    value={uploadData.title}
                    onChange={(e) => setUploadData({ ...uploadData, title: e.target.value })}
                    required
                    className="w-full px-3 py-2 border border-[#3d4a35] rounded-lg bg-[#1a1f16] text-[#c5bfa9] focus:ring-2 focus:ring-[#5a6f4e] focus:border-transparent"
                    placeholder="Enter image title"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#8b9a7e] mb-1">
                    Alt Text (for SEO)
                  </label>
                  <input
                    type="text"
                    value={uploadData.alt_text}
                    onChange={(e) => setUploadData({ ...uploadData, alt_text: e.target.value })}
                    className="w-full px-3 py-2 border border-[#3d4a35] rounded-lg bg-[#1a1f16] text-[#c5bfa9] focus:ring-2 focus:ring-[#5a6f4e] focus:border-transparent"
                    placeholder="Describe the image"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-[#8b9a7e] mb-1">
                  Image *
                </label>
                <FileUpload
                  currentFile={uploadData.image_url}
                  onFileUploaded={(url) => setUploadData({ ...uploadData, image_url: url })}
                />
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <Button 
                  type="button" 
                  variant="outline"
                  className="border-[#3d4a35] text-[#8b9a7e] hover:bg-[#232921]"
                  onClick={() => {
                    setShowUpload(false);
                    setEditingId(null);
                    setUploadData({ title: '', image_url: '', alt_text: '', order: 0 });
                  }}
                >
                  Cancel
                </Button>
                <Button type="submit" className="bg-[#5a6f4e] hover:bg-[#4a5f3e] text-white">
                  {editingId ? 'Update Image' : 'Add Image'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Gallery Preview */}
      <div className="mb-4">
        <h2 className="text-lg font-semibold text-[#c5bfa9]">
          Gallery Images ({images.length})
        </h2>
        <p className="text-sm text-[#8b9a7e]">Drag to reorder. These images will appear in the carousel on the website.</p>
      </div>

      {/* Images Grid */}
      {images.length === 0 ? (
        <Card className="bg-[#232921] border-dashed border-2 border-[#3d4a35]">
          <CardContent className="p-12 text-center">
            <div className="text-[#5a6f4e] mb-4">
              <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
            <p className="text-[#c5bfa9] text-lg">No gallery images yet</p>
            <p className="text-[#8b9a7e] mt-1">Click "Add Image" to upload your first gallery image</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {images.map((image, index) => (
            <Card key={image.id} className="bg-[#232921] border border-[#3d4a35] overflow-hidden group">
              <div className="relative aspect-video">
                <img 
                  src={getImageUrl(image.image_url)} 
                  alt={image.alt_text || image.title}
                  className="w-full h-full object-cover"
                />
                {/* Order badge */}
                <div className="absolute top-2 left-2 bg-black/70 text-white text-xs px-2 py-1 rounded">
                  #{index + 1}
                </div>
                {/* Action buttons overlay */}
                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                  <button
                    onClick={() => handleReorder(image.id, 'up')}
                    disabled={index === 0}
                    className="p-2 bg-white/90 rounded-full hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Move up"
                  >
                    <ChevronUp size={18} className="text-gray-700" />
                  </button>
                  <button
                    onClick={() => handleReorder(image.id, 'down')}
                    disabled={index === images.length - 1}
                    className="p-2 bg-white/90 rounded-full hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Move down"
                  >
                    <ChevronDown size={18} className="text-gray-700" />
                  </button>
                  <button
                    onClick={() => handleEdit(image)}
                    className="p-2 bg-white/90 rounded-full hover:bg-white"
                    title="Edit"
                  >
                    <Edit2 size={18} className="text-gray-700" />
                  </button>
                  <button
                    onClick={() => handleDelete(image.id)}
                    className="p-2 bg-red-500/90 rounded-full hover:bg-red-500"
                    title="Delete"
                  >
                    <Trash2 size={18} className="text-white" />
                  </button>
                </div>
              </div>
              <CardContent className="p-3">
                <h3 className="font-medium text-[#c5bfa9] truncate">{image.title}</h3>
                {image.alt_text && (
                  <p className="text-sm text-[#8b9a7e] truncate">{image.alt_text}</p>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminGalleryNew;
